import {
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Heading,
  Spinner,
  Switch,
  Text,
  Flex,
  CheckboxGroup,
} from '@chakra-ui/react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import {
  EntityTag,
  UpdateTagRequest,
  tagsApi,
} from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { SubmitButton } from '../shared/form/SubmitButton';
import { TexasFormLabel } from '../shared/form/TexasFormLabel';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SharedDisclosureProps } from '../shared/types';
import { RoleRestrict } from '../shared/RoleRestrict';
import { Role } from '@texas/api/endpoints/userApi';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { tagEvents } from './events';
import { EntityTypeCheckboxes } from './EntityTypeCheckboxes';

export interface UpdateTagModalProps extends SharedDisclosureProps {
  tagId: number;
  onTagUpdated: (tag: EntityTag) => void;
}

export function UpdateTagModal({
  isOpen,
  onClose,
  tagId,
  onTagUpdated,
}: UpdateTagModalProps) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading size="md">{t('tags.edit')}</Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Form tagId={tagId} onClose={onClose} onTagUpdated={onTagUpdated} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function Form({
  tagId,
  onClose,
  onTagUpdated,
}: {
  tagId: number;
  onClose: () => void;
  onTagUpdated: (tag: EntityTag) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: tag, refetch, loading, error } = useApiResource(tagsApi.get);

  const {
    request: updateTagRequest,
    loading: updating,
    error: updateError,
  } = useApiRequest(tagsApi.update);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<UpdateTagRequest>();

  const labelValue = useWatch({
    control,
    name: 'label',
    defaultValue: '',
  });

  const isShared = useWatch({
    control,
    name: 'isShared',
    defaultValue: false,
  });

  useEffect(() => {
    refetch(tagId);
  }, [refetch, tagId]);

  useEffect(() => {
    if (tag) {
      reset({
        label: tag.label,
        color: tag.color,
        systemTag: tag.systemTag,
        isShared: tag.userId === null,
        applicableEntityTypes:
          tag.applicableEntityTypes?.map((et) =>
            ensureEnumNumber(EntityType, et),
          ) ?? null,
      });
    }
  }, [reset, tag]);

  const onSubmit = async (data: UpdateTagRequest) =>
    await request(updateTagRequest, [tagId, data], (updatedTag: EntityTag) => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      onTagUpdated(updatedTag);
      tagEvents.tagUpdated.dispatch();
      onClose();
    });

  return (
    <>
      {loading && <Spinner />}
      {error && <ErrorDetails error={error} />}
      {updateError && <ErrorDetails error={updateError} />}
      {!loading && tag && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <VStack spacing={4} pb={2}>
            <FormControl isRequired={true} isInvalid={!!errors.label}>
              <TexasFormLabel>{t('tags.label')}</TexasFormLabel>
              <Input
                variant="outline"
                {...register('label', {
                  required: true,
                  maxLength: {
                    value: 100,
                    message: t('errors.maxLength', { count: 100 }),
                  },
                })}
                placeholder={t('tags.label')}
                autoFocus={true}
              />
              <FormCounter length={labelValue.length} maxLength={100} />
              <ErrorLabel text={errors.label?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.color}>
              <TexasFormLabel>{t('tags.color')}</TexasFormLabel>
              <Input
                type="color"
                {...register('color', {
                  required: true,
                })}
                height={10}
                padding={1}
                cursor="pointer"
              />
              <ErrorLabel text={errors.color?.message} />
            </FormControl>
            <RoleRestrict allow={[Role.SystemAdministrator]}>
              <FormControl>
                <Flex justifyContent="space-between">
                  <Text>{t('tags.isShared')}</Text>
                  <Switch {...register('isShared')} />
                </Flex>
              </FormControl>
              {isShared && (
                <FormControl>
                  <TexasFormLabel>
                    {t('tags.applicableEntityTypes')}
                  </TexasFormLabel>
                  <Text fontSize="sm" color="gray.400" mb={2}>
                    {t('tags.applicableEntityTypesDescription')}
                  </Text>
                  <Controller
                    name="applicableEntityTypes"
                    control={control}
                    render={({ field }) => (
                      <CheckboxGroup
                        value={field.value?.map(String) ?? []}
                        onChange={(values) => {
                          const entityTypes =
                            values.length > 0
                              ? values.map((v) => Number(v) as EntityType)
                              : null;
                          field.onChange(entityTypes);
                        }}
                      >
                        <EntityTypeCheckboxes />
                      </CheckboxGroup>
                    )}
                  />
                </FormControl>
              )}
            </RoleRestrict>
            <SubmitButton loading={updating}>
              {t('general.update')}
            </SubmitButton>
          </VStack>
        </form>
      )}
    </>
  );
}
